import useGetDashboardSummary from '@/api/queries/dashboard/useGetDashboardSummary';
import { currencyLabel } from '@/helpers/currency';
import { useTranslation } from '@/i18n';
import { useRegion } from '@/api/rest/users/useRegion';
import { currencyCodeForRegion } from '@payaca/helpers/currencyHelper';
import { FC } from 'react';
import { DateRange } from '../utils';
import DashboardSectionError from './DashboardSectionError';
import DashboardSectionSkeleton from './DashboardSectionSkeleton';
import MetricCard from './MetricCard';

interface DashboardSummaryProps {
  dateRange?: DateRange;
  comparisonPeriodLabel?: string;
}

const DashboardSummary: FC<DashboardSummaryProps> = ({
  dateRange,
  comparisonPeriodLabel,
}) => {
  const region = useRegion();
  const translateErrors = useTranslation('dashboard.errors');
  const translate = useTranslation('dashboard.summary');
  const { summary, isLoading, isError } = useGetDashboardSummary({ dateRange });

  // Only show comparison when we have a defined date range (not "all-time")
  const showComparison = Boolean(dateRange?.from && dateRange?.to);

  const currencyCode = currencyCodeForRegion[region];
  const formatCurrency = (value: number) => currencyLabel(value, currencyCode);

  if (isLoading) {
    return <DashboardSectionSkeleton variant="summary" />;
  }

  if (isError) {
    return <DashboardSectionError message={translateErrors('summaryError')} />;
  }

  if (!summary) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 @screen-sm:grid-cols-3">
      <MetricCard
        title={translate('revenueSecured')}
        value={summary.revenueSecured.value.value}
        previousValue={
          showComparison
            ? summary.revenueSecured.previousValue.value
            : undefined
        }
        percentageChange={
          showComparison ? summary.revenueSecured.percentageChange : undefined
        }
        formatValue={formatCurrency}
        sparkline={summary.revenueSecured.sparkline}
        color="#22c55e" // green-500
        emptyMessage={translate('revenueSecuredEmpty')}
        tooltip={translate('revenueSecuredTooltip')}
        region={region}
        comparisonPeriodLabel={
          showComparison ? comparisonPeriodLabel : undefined
        }
      />
      <MetricCard
        title={translate('revenueReceived')}
        value={summary.revenueReceived.value.value}
        previousValue={
          showComparison
            ? summary.revenueReceived.previousValue.value
            : undefined
        }
        percentageChange={
          showComparison ? summary.revenueReceived.percentageChange : undefined
        }
        formatValue={formatCurrency}
        sparkline={summary.revenueReceived.sparkline}
        color="#3b82f6" // blue-500
        emptyMessage={translate('revenueReceivedEmpty')}
        tooltip={translate('revenueReceivedTooltip')}
        region={region}
        comparisonPeriodLabel={
          showComparison ? comparisonPeriodLabel : undefined
        }
      />
      <MetricCard
        title={translate('overdueTitle')}
        value={summary.outstanding.value.value}
        formatValue={formatCurrency}
        sparkline={summary.outstanding.sparkline}
        color="#eab308" // yellow-500
        isWarning
        tooltip={translate('overdueTooltip')}
        region={region}
      />
    </div>
  );
};

export default DashboardSummary;
